import { useQuery, useMutation, type UseQueryOptions, type UseMutationOptions } from '@tanstack/react-query';
import type { ApiError } from '@/api/client';

/**
 * Thin wrappers around TanStack Query that:
 *   - default `staleTime` to 30s (the API mostly returns derived state),
 *   - ensure the error type surfaces as `ApiError` for consumers,
 *   - default mutation `retry` to false (mutations should not auto-retry).
 *
 * Rule for the codebase:
 *   - Use `useApiQuery` for reads.
 *   - Use `useApiMutation` for any state change.
 */

export function useApiQuery<TData>(
  options: UseQueryOptions<TData, ApiError, TData>,
) {
  return useQuery<TData, ApiError, TData>({
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useApiMutation<TData, TVariables = void>(
  options: UseMutationOptions<TData, ApiError, TVariables>,
) {
  return useMutation<TData, ApiError, TVariables>({
    retry: false,
    ...options,
  });
}
